/* eslint-disable react/no-array-index-key */
import { Box, createStyles, getDefaultZIndex, Tooltip } from '@mantine/core';
import { KeyboardShortcut } from '@repo/common/components/KeyboardShortcut/KeyboardShortcut';
import { observer } from 'mobx-react-lite';
import { useSecodaEditor } from '../../context';
import type { MenuItem } from '../../types';
import ToolbarButton from './ToolbarButton';
import { ToolbarButtonMenu } from './ToolbarButtonMenu';
import ToolbarSeparator from './ToolbarSeparator';

const useStyles = createStyles((theme) => ({
	wrapper: {
		willChange: 'opacity, transform',
		padding: theme.spacing['3xs'],
		gap: theme.spacing['3xs'],
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		zIndex: getDefaultZIndex('max'),
		backgroundColor: theme.other.getColor('surface/primary/default'),
		border: `${theme.other.borderWidth.xs}px solid ${theme.other.getColor('border/primary/default')}`,
		borderRadius: theme.other.borderRadius.md,
		boxShadow: theme.shadows.md,
		transition:
			'opacity 150ms cubic-bezier(0.175, 0.885, 0.32, 1.275), transform 150ms cubic-bezier(0.175, 0.885, 0.32, 1.275)',
		transitionDelay: '150ms',
		lineHeight: 0,
		pointerEvents: 'none',
		whiteSpace: 'nowrap',

		'@media print': {
			display: 'none',
		},

		'@media (hover: none) and (pointer: coarse)': {
			transition: 'opacity 150ms cubic-bezier(0.175, 0.885, 0.32, 1.275)',
			transform: 'scale(1)',
			borderRadius: 0,
			width: '100vw',
			position: 'fixed',
		},
	},
}));

interface ToolbarMenuProps {
	items: MenuItem[];
}

export const ToolbarMenu = observer((props: ToolbarMenuProps) => {
	const { classes } = useStyles();
	const editorState = useSecodaEditor();
	const { items } = props;

	if (!editorState.view) {
		return null;
	}

	return (
		<Box className={classes.wrapper}>
			{items.map((item, index) => {
				if (item.name === 'separator' && item.visible !== false) {
					return <ToolbarSeparator key={index} />;
				}
				if (item.visible === false || !item.icon) {
					return null;
				}

				const Icon = item.icon;
				const isActive = item.active
					? item.active(editorState.view!.state)
					: false;
				const isMenu = !!item.menuOptions;

				if (isMenu) {
					return (
						<ToolbarButtonMenu
							key={index}
							item={item}
							view={editorState.view!}
							onClick={(option) =>
								option.name && editorState.commands[option.name](option.attrs)
							}
						/>
					);
				}

				return (
					<Tooltip
						key={index}
						label={
							item.shortcutKeys ? (
								<KeyboardShortcut keys={item.shortcutKeys}>
									{item.tooltip}
								</KeyboardShortcut>
							) : (
								item.tooltip
							)
						}
						position="top"
					>
						<ToolbarButton
							data-testid={`selection-toolbar-${item.name}-c1099ed`}
							onClick={() =>
								item.name && editorState.commands[item.name](item.attrs)
							}
							data-active={isActive ? true : undefined}
						>
							<Icon />
						</ToolbarButton>
					</Tooltip>
				);
			})}
		</Box>
	);
});
